import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from '@/components/ui/sheet';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { PortalButton } from '@/components/billing/PortalButton';
import { getCapabilityColors, capabilityTitleIconBackground } from '@/lib/capability-colors';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

import {
    Bell,
    CalendarDays,
    Plus,
    RefreshCw,
    Package,
    Gauge,
    Layers,
    Clock,
    Key,
    Trash2,
    Copy,
    Check,
    AlertTriangle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SubscriptionItem {
    id: string;
    feature_price_id: string;
    feature_name?: string;
    pricing_model?: string;
    unit_amount?: number;
    currency?: string;
    current_usage?: number;
    usage_limit?: number;
}

interface Subscription {
    id: string;
    product_id: string;
    status: string;
    stripe_subscription_id?: string;
    current_period_start?: string;
    current_period_end?: string;
    canceled_at?: string;
    created: string;
    items?: SubscriptionItem[];
}

interface Product {
    id: string;
    title: string;
    summary?: string;
    description?: string;
}

interface FeaturePrice {
    id: string;
    product_id?: string;
    capability_id?: string;
    capability_key?: string;
    capability_type?: string;
    capability_name?: string;
    capability_description?: string;
    name?: string;
    description?: string;
    feature_name?: string;
    tier_name?: string;
    pricing_model: string;
    amount_cents?: number;
    unit_amount?: number;
    currency: string;
    billing_interval?: string;
    usage_limit?: number;
    overage_unit_amount_cents?: number;
    metric?: string;
    is_base_price?: boolean;
    status?: string;
}

type StatusKey = 'active' | 'trialing' | 'past_due' | 'canceled';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<StatusKey, string> = {
    active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    trialing: 'bg-blue-100 text-blue-800 border-blue-200',
    past_due: 'bg-amber-100 text-amber-800 border-amber-200',
    canceled: 'bg-red-100 text-red-800 border-red-200',
};

function statusBadge(status: string) {
    const style = STATUS_STYLES[status as StatusKey] ?? 'bg-slate-100 text-slate-800';
    return <Badge className={style}>{status.replace('_', ' ')}</Badge>;
}

function pricingModelBadgeVariant(model: string): string {
    switch (model) {
        case 'flat':
            return 'bg-blue-100 text-blue-800';
        case 'metered':
            return 'bg-amber-100 text-amber-800';
        case 'per_unit':
            return 'bg-purple-100 text-purple-800';
        default:
            return 'bg-slate-100 text-slate-800';
    }
}

function formatCents(cents?: number): string {
    if (cents == null) return '—';
    return `$${(cents / 100).toFixed(2)}`;
}

function pricingLabel(fp: FeaturePrice): string {
    const amount = fp.amount_cents ?? fp.unit_amount;
    const interval = fp.billing_interval ?? 'month';
    switch (fp.pricing_model) {
        case 'flat':
            return `${formatCents(amount)}/${interval}`;
        case 'metered':
            return `${formatCents(amount)}/invocation`;
        case 'per_unit':
            return `${formatCents(amount)}/unit/${interval}`;
        default:
            return formatCents(amount);
    }
}

function formatDate(d?: string): string {
    return d ? new Date(d).toLocaleDateString() : '\u2014';
}

interface SubscriberKeyItem {
    id: string;
    client_id: string;
    client_name: string;
    description?: string;
    created_at: string;
}

interface CreatedKeyCredentials {
    client_id: string;
    client_secret: string;
    client_name: string;
    description?: string;
    created_at: string;
}

function featureCountLabel(items?: SubscriptionItem[]): string {
    if (!items || items.length === 0) return 'No features';
    const base = items.filter((i) => i.pricing_model === 'flat');
    const extras = items.length - base.length;
    if (base.length > 0 && extras > 0) return `Base + ${extras} feature${extras > 1 ? 's' : ''}`;
    return `${items.length} feature${items.length > 1 ? 's' : ''}`;
}

// ---------------------------------------------------------------------------
// Sort / filter constants
// ---------------------------------------------------------------------------

const SORT_OPTIONS = [
    { key: 'product', label: 'Product' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
];

const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'trialing', label: 'Trialing' },
    { value: 'past_due', label: 'Past Due' },
    { value: 'canceled', label: 'Canceled' },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SubscriptionsPage() {
    usePageTitle('Subscriptions');
    const { currentOrganization } = useAuth();
    const { setHeaderTitle } = useHeaderAction();
    const orgId = currentOrganization?.id;

    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [productCache, setProductCache] = useState<Record<string, Product>>({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('created');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [statusFilter, setStatusFilter] = useState('all');

    // detail drawer
    const [detailSub, setDetailSub] = useState<Subscription | null>(null);
    const [detailProduct, setDetailProduct] = useState<Product | null>(null);
    const [detailFeatures, setDetailFeatures] = useState<FeaturePrice[]>([]);
    const [availableFeatures, setAvailableFeatures] = useState<FeaturePrice[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // add feature
    const [addFeatureId, setAddFeatureId] = useState<string>('');
    const [addingFeature, setAddingFeature] = useState(false);

    // cancel
    const [cancelTarget, setCancelTarget] = useState<Subscription | null>(null);
    const [canceling, setCanceling] = useState(false);

    // subscriber API keys (bound to this marketplace subscription)
    const [subKeys, setSubKeys] = useState<SubscriberKeyItem[]>([]);
    const [keysLoading, setKeysLoading] = useState(false);
    const [createKeyOpen, setCreateKeyOpen] = useState(false);
    const [createKeyName, setCreateKeyName] = useState('');
    const [createKeyDesc, setCreateKeyDesc] = useState('');
    const [creatingKey, setCreatingKey] = useState(false);
    const [keyCredentials, setKeyCredentials] = useState<CreatedKeyCredentials | null>(null);
    const [copiedId, setCopiedId] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
    const [revokingKey, setRevokingKey] = useState(false);

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    const fetchSubscriptions = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/billing/subscriptions', {
                params: { org_id: orgId },
            });
            const raw = res.data?.subscriptions ?? res.data;
            const subs: Subscription[] = Array.isArray(raw) ? raw : [];
            setSubscriptions(subs);

            const productIds = [...new Set(subs.map((s) => s.product_id))];
            const newProducts: Record<string, Product> = {};
            await Promise.all(
                productIds.map(async (pid) => {
                    if (productCache[pid]) {
                        newProducts[pid] = productCache[pid];
                        return;
                    }
                    try {
                        const pRes = await api.get(`/billing/products/${pid}`);
                        const pData = pRes.data as { product?: Product } & Product;
                        newProducts[pid] = pData.product ?? pData;
                    } catch {
                        newProducts[pid] = { id: pid, title: pid };
                    }
                }),
            );
            setProductCache((prev) => ({ ...prev, ...newProducts }));
        } catch (err) {
            console.error('SubscriptionsPage:fetchSubscriptions - failed', err);
            toast.error('Failed to load subscriptions');
        } finally {
            setLoading(false);
        }
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    // Header icon + subtitle
    const titleIcon = useMemo(() => {
        const colors = getCapabilityColors('subscription');
        const iconSurfaceStyle = capabilityTitleIconBackground(colors.hex, true);
        return (
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${colors.text}`}
                style={iconSurfaceStyle}
            >
                <Bell size={16} />
            </div>
        );
    }, []);

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: 'Subscriptions',
            subtitle: 'View and manage your active subscriptions and billing features.',
            intense: true,
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle, titleIcon]);

    const openDetail = async (sub: Subscription) => {
        setDetailSub(sub);
        setLoadingDetail(true);
        setAddFeatureId('');
        setSubKeys([]);
        setKeysLoading(true);

        try {
            const [productRes, featuresRes, keysRes] = await Promise.all([
                api.get(`/billing/products/${sub.product_id}`),
                api.get(`/billing/products/${sub.product_id}/features`),
                api.get<{ api_keys: SubscriberKeyItem[] }>(`/subscriptions/${sub.id}/keys`).catch(() => ({ data: { api_keys: [] } })),
            ]);
            const productData = productRes.data as { product?: Product } & Product;
            setDetailProduct(productData.product ?? productData);
            const featuresRaw = featuresRes.data?.features ?? featuresRes.data;
            const allFeatures: FeaturePrice[] = Array.isArray(featuresRaw) ? featuresRaw : [];
            setDetailFeatures(allFeatures);
            const subscribedIds = new Set((sub.items ?? []).map((i) => i.feature_price_id));
            setAvailableFeatures(allFeatures.filter((f) => !subscribedIds.has(f.id)));
            setSubKeys(keysRes.data?.api_keys ?? []);
        } catch (err) {
            console.error('SubscriptionsPage:openDetail - failed to load details', err);
            toast.error('Failed to load subscription details');
        } finally {
            setLoadingDetail(false);
            setKeysLoading(false);
        }
    };

    const fetchSubKeys = async (subscriptionId: string) => {
        setKeysLoading(true);
        try {
            const { data } = await api.get<{ api_keys: SubscriberKeyItem[] }>(
                `/subscriptions/${subscriptionId}/keys`,
            );
            setSubKeys(data.api_keys ?? []);
        } catch {
            setSubKeys([]);
        } finally {
            setKeysLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!detailSub || !createKeyName.trim()) return;
        setCreatingKey(true);
        try {
            const { data } = await api.post<CreatedKeyCredentials>(
                `/subscriptions/${detailSub.id}/keys`,
                {
                    display_name: createKeyName.trim(),
                    description: createKeyDesc.trim() || undefined,
                },
            );
            setKeyCredentials(data);
        } catch (err: unknown) {
            const msg =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                'Failed to create API key';
            toast.error(msg);
            console.error('SubscriptionsPage:handleCreateKey - failed', err);
        } finally {
            setCreatingKey(false);
        }
    };

    const closeCreateKey = () => {
        if (keyCredentials && detailSub) {
            void fetchSubKeys(detailSub.id);
        }
        setCreateKeyOpen(false);
        setCreateKeyName('');
        setCreateKeyDesc('');
        setKeyCredentials(null);
        setCopiedId(false);
        setCopiedSecret(false);
    };

    const handleRevokeKey = async () => {
        if (!detailSub || !revokeKeyId) return;
        setRevokingKey(true);
        try {
            await api.delete(`/subscriptions/${detailSub.id}/keys/${revokeKeyId}`);
            toast.success('API key revoked');
            setRevokeKeyId(null);
            void fetchSubKeys(detailSub.id);
        } catch (err) {
            toast.error('Failed to revoke API key');
            console.error('SubscriptionsPage:handleRevokeKey - failed', err);
        } finally {
            setRevokingKey(false);
        }
    };

    const copyKeyCredential = async (text: string, type: 'id' | 'secret') => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'id') {
                setCopiedId(true);
                setTimeout(() => setCopiedId(false), 2000);
            } else {
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        } catch {
            /* ignore */
        }
    };

    const closeDetail = () => {
        setDetailSub(null);
        setDetailProduct(null);
        setDetailFeatures([]);
        setAvailableFeatures([]);
    };

    // -----------------------------------------------------------------------
    // Mutations
    // -----------------------------------------------------------------------

    const handleAddFeature = async () => {
        if (!detailSub || !addFeatureId) return;
        setAddingFeature(true);
        try {
            await api.post(`/billing/subscriptions/${detailSub.id}/update-items`, {
                add_feature_price_ids: [addFeatureId],
                remove_feature_price_ids: [],
            });
            toast.success('Feature added to subscription');
            setAddFeatureId('');
            await fetchSubscriptions();
            const refreshed = subscriptions.find((s) => s.id === detailSub.id);
            if (refreshed) openDetail(refreshed);
        } catch (err) {
            console.error('SubscriptionsPage:handleAddFeature - failed', err);
            toast.error('Failed to add feature');
        } finally {
            setAddingFeature(false);
        }
    };

    const handleCancel = async () => {
        if (!cancelTarget) return;
        setCanceling(true);
        try {
            await api.post(`/billing/subscriptions/${cancelTarget.id}/cancel`);
            toast.success('Subscription canceled');
            setCancelTarget(null);
            closeDetail();
            await fetchSubscriptions();
        } catch (err) {
            console.error('SubscriptionsPage:handleCancel - cancel failed', err);
            toast.error('Failed to cancel subscription');
        } finally {
            setCanceling(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const productName = (productId: string) => productCache[productId]?.title ?? productId;
    const productDescription = (productId: string) =>
        productCache[productId]?.summary ?? productCache[productId]?.description;

    const isActive = (status: string) => status === 'active' || status === 'trialing';

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredSubscriptions = useMemo(() => {
        let result = subscriptions;

        if (statusFilter !== 'all') {
            result = result.filter((sub) => sub.status === statusFilter);
        }

        const q = searchQuery.trim().toLowerCase();
        if (q) {
            result = result.filter((sub) => {
                const title = productName(sub.product_id).toLowerCase();
                const desc = (productDescription(sub.product_id) ?? '').toLowerCase();
                return (
                    title.includes(q) ||
                    desc.includes(q) ||
                    sub.status.toLowerCase().includes(q) ||
                    sub.id.toLowerCase().includes(q)
                );
            });
        }

        const sorted = [...result].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'product': {
                    const nameA = productName(a.product_id).toLowerCase();
                    const nameB = productName(b.product_id).toLowerCase();
                    cmp = nameA.localeCompare(nameB);
                    break;
                }
                case 'status':
                    cmp = a.status.localeCompare(b.status);
                    break;
                case 'created':
                default:
                    cmp = new Date(a.created).getTime() - new Date(b.created).getTime();
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [subscriptions, searchQuery, statusFilter, sortField, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

    // -----------------------------------------------------------------------
    // Loading state
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <div className="p-6">
                <Card className="p-8">
                    <div className="flex items-center justify-center gap-3">
                        <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                        <p className="text-muted-foreground">Loading subscriptions...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Main render
    // -----------------------------------------------------------------------

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-6">
            <CapabilityListPanel<Subscription>
                items={filteredSubscriptions}
                sortOptions={SORT_OPTIONS}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search subscriptions by product, status, or id..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                extraToolbar={
                    <div className="flex items-center gap-2">
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_FILTER_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <PortalButton label="Manage Billing" />
                    </div>
                }
                emptyStateVariant="glass"
                emptyMessage="No subscriptions yet. Browse the marketplace to subscribe."
                renderCard={(sub) => {
                    const active = isActive(sub.status);
                    const desc = productDescription(sub.product_id);
                    return (
                        <button
                            key={sub.id}
                            type="button"
                            onClick={() => openDetail(sub)}
                            className="block w-full text-left"
                        >
                            <Card
                                className={`relative w-full min-h-32 border border-slate-200 border-l-4 p-3 ${
                                    active ? 'border-l-violet-400' : 'border-l-slate-300'
                                } text-foreground/90 shadow-sm transition-all duration-200 hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow-md hover:shadow-violet-100/50`}
                            >
                                <div className="absolute right-2 top-2 z-10">
                                    {statusBadge(sub.status)}
                                </div>
                                <div className="flex flex-row items-start gap-2">
                                    <div className="flex shrink-0 items-center justify-center rounded-md bg-violet-50 p-1.5 text-violet-600">
                                        <Package size={18} />
                                    </div>
                                    <div className="flex flex-1 flex-col">
                                        <div className="text-left text-sm font-bold leading-tight text-slate-800">
                                            {productName(sub.product_id)}
                                        </div>
                                        {desc && (
                                            <div className="mt-0.5 line-clamp-2 text-left text-[10px] text-slate-500">
                                                {desc}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-auto flex items-center gap-3 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500">
                                    <div className="flex items-center gap-1 text-slate-500">
                                        <CalendarDays size={10} />
                                        <span>
                                            {formatDate(sub.current_period_start)} &mdash;{' '}
                                            {formatDate(sub.current_period_end)}
                                        </span>
                                    </div>
                                    <div className="ml-auto flex items-center gap-1 text-slate-400">
                                        <Clock size={10} />
                                        <span>{formatDate(sub.created)}</span>
                                    </div>
                                </div>
                            </Card>
                        </button>
                    );
                }}
            />

            {/* Usage summary for metered subscriptions */}
            {subscriptions.some(
                (s) =>
                    isActive(s.status) &&
                    s.items?.some((i) => i.pricing_model === 'metered' && i.usage_limit),
            ) && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Gauge className="h-5 w-5 text-slate-600" />
                            Usage Summary
                        </CardTitle>
                        <CardDescription>
                            Current period metered usage across your subscriptions
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {subscriptions
                            .filter((s) => isActive(s.status))
                            .flatMap((s) =>
                                (s.items ?? [])
                                    .filter(
                                        (i) =>
                                            i.pricing_model === 'metered' && i.usage_limit,
                                    )
                                    .map((item) => ({ sub: s, item })),
                            )
                            .map(({ sub, item }) => {
                                const used = item.current_usage ?? 0;
                                const limit = item.usage_limit ?? 1;
                                const pct = Math.min((used / limit) * 100, 100);
                                return (
                                    <div key={`${sub.id}-${item.id}`} className="space-y-1">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-medium text-slate-700">
                                                {item.feature_name ?? item.feature_price_id}
                                            </span>
                                            <span className="text-slate-500">
                                                {used.toLocaleString()} / {limit.toLocaleString()}
                                            </span>
                                        </div>
                                        <Progress
                                            value={pct}
                                            className={
                                                pct >= 90
                                                    ? '[&>div]:bg-red-500'
                                                    : pct >= 70
                                                      ? '[&>div]:bg-amber-500'
                                                      : ''
                                            }
                                        />
                                    </div>
                                );
                            })}
                    </CardContent>
                </Card>
            )}

            {/* ----------------------------------------------------------------- */}
            {/* Detail Drawer                                                       */}
            {/* ----------------------------------------------------------------- */}
            <SlideDrawer isOpen={!!detailSub} onClose={closeDetail}>
                    <div className="shrink-0 border-b border-slate-100 bg-slate-50/50 px-8 py-6">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                                <Package size={20} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-xl font-semibold text-slate-900">
                                    {detailProduct?.title ?? detailSub?.product_id ?? 'Subscription'}
                                </h2>
                                {detailProduct?.summary && (
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                        {detailProduct.summary}
                                    </p>
                                )}
                                <div className="mt-2 flex items-center gap-2">
                                    {detailSub && statusBadge(detailSub.status)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {loadingDetail ? (
                        <div className="flex flex-1 items-center justify-center py-8 gap-3">
                            <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                            <span className="text-muted-foreground">Loading details...</span>
                        </div>
                    ) : (
                        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-24 pb-2">
                            {/* Subscription Details */}
                            <div>
                                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                                    Subscription Details
                                </h3>
                                <Card className="border border-slate-200 bg-white shadow-sm">
                                    <CardContent className="py-4">
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Current Period</p>
                                                <p className="font-medium text-slate-800">
                                                    {formatDate(detailSub?.current_period_start)} &mdash;{' '}
                                                    {formatDate(detailSub?.current_period_end)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Created</p>
                                                <p className="font-medium text-slate-800">
                                                    {formatDate(detailSub?.created)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Features</p>
                                                <p className="font-medium text-slate-800">
                                                    {featureCountLabel(detailSub?.items)}
                                                </p>
                                            </div>
                                            {detailSub?.canceled_at && (
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Canceled At</p>
                                                    <p className="font-medium text-red-600">
                                                        {formatDate(detailSub.canceled_at)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Capability / Feature Cards */}
                            {(() => {
                                const subscribedIds = new Set(
                                    (detailSub?.items ?? []).map((i) => i.feature_price_id),
                                );
                                const subscribedFeatures = detailFeatures.filter(
                                    (f) => subscribedIds.has(f.id),
                                );
                                const itemMap = new Map(
                                    (detailSub?.items ?? []).map((i) => [i.feature_price_id, i]),
                                );

                                return (
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-900 mb-4">
                                            Features
                                        </h3>
                                        {subscribedFeatures.length === 0 ? (
                                            <Card className="border border-slate-200 bg-white shadow-sm">
                                                <CardContent className="py-12 text-center">
                                                    <Layers className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                                    <p className="text-muted-foreground">
                                                        No capability features on this subscription.
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        ) : (
                                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                {subscribedFeatures.map((fp) => {
                                                    const capType = fp.capability_type ?? 'app';
                                                    const colors = getCapabilityColors(capType);
                                                    const Icon = colors.icon;
                                                    const capDescription =
                                                        fp.capability_description || fp.description || null;
                                                    const metricLabel = fp.metric
                                                        ? fp.metric.replace(/_/g, ' ')
                                                        : null;
                                                    const subItem = itemMap.get(fp.id);
                                                    const used = subItem?.current_usage ?? 0;
                                                    const limit = fp.usage_limit ?? subItem?.usage_limit ?? 0;
                                                    const pct =
                                                        limit > 0
                                                            ? Math.min((used / limit) * 100, 100)
                                                            : 0;

                                                    return (
                                                        <Card
                                                            key={fp.id}
                                                            className={`relative p-5 min-h-30 border-l-4 ${colors.border} transition-colors`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div
                                                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors.bgLight} ${colors.text}`}
                                                                >
                                                                    <Icon size={20} className={colors.text} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-slate-900 truncate">
                                                                        {fp.name ||
                                                                            fp.capability_name ||
                                                                            fp.feature_name ||
                                                                            fp.capability_key ||
                                                                            'Unknown'}
                                                                    </p>
                                                                    {capDescription && (
                                                                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                                                            {capDescription}
                                                                        </p>
                                                                    )}
                                                                    {fp.tier_name && (
                                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                                            Tier: {fp.tier_name}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="mt-3 space-y-1">
                                                                <div className="flex items-baseline gap-2">
                                                                    <span className="text-lg font-semibold text-slate-900">
                                                                        {pricingLabel(fp)}
                                                                    </span>
                                                                    <Badge
                                                                        className={`text-[10px] capitalize ${pricingModelBadgeVariant(fp.pricing_model)}`}
                                                                    >
                                                                        {fp.pricing_model.replace(/_/g, ' ')}
                                                                    </Badge>
                                                                </div>

                                                                <div className="space-y-0.5">
                                                                    {fp.pricing_model === 'metered' && (
                                                                        <>
                                                                            {metricLabel && (
                                                                                <p className="text-xs text-slate-500">
                                                                                    Billed per {metricLabel}
                                                                                </p>
                                                                            )}
                                                                            {limit > 0 && (
                                                                                <p className="text-xs text-slate-500">
                                                                                    {limit.toLocaleString()}{' '}
                                                                                    {metricLabel ?? 'units'} included
                                                                                </p>
                                                                            )}
                                                                            {fp.overage_unit_amount_cents != null &&
                                                                                fp.overage_unit_amount_cents > 0 && (
                                                                                    <p className="text-xs text-slate-500">
                                                                                        Overage:{' '}
                                                                                        {formatCents(
                                                                                            fp.overage_unit_amount_cents,
                                                                                        )}
                                                                                        /
                                                                                        {metricLabel
                                                                                            ? metricLabel.replace(/s$/, '')
                                                                                            : 'unit'}
                                                                                    </p>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                    {fp.pricing_model === 'per_unit' && (
                                                                        <>
                                                                            {limit > 0 && (
                                                                                <p className="text-xs text-slate-500">
                                                                                    Up to {limit.toLocaleString()} units/
                                                                                    {fp.billing_interval ?? 'month'}
                                                                                </p>
                                                                            )}
                                                                            {fp.overage_unit_amount_cents != null &&
                                                                                fp.overage_unit_amount_cents > 0 && (
                                                                                    <p className="text-xs text-slate-500">
                                                                                        Overage:{' '}
                                                                                        {formatCents(
                                                                                            fp.overage_unit_amount_cents,
                                                                                        )}
                                                                                        /unit
                                                                                    </p>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                    {fp.pricing_model === 'flat' &&
                                                                        fp.billing_interval && (
                                                                            <p className="text-xs text-slate-500">
                                                                                Billed{' '}
                                                                                {fp.billing_interval === 'year'
                                                                                    ? 'annually'
                                                                                    : 'monthly'}
                                                                            </p>
                                                                        )}
                                                                </div>

                                                                {/* Usage progress for metered items */}
                                                                {(fp.pricing_model === 'metered' ||
                                                                    fp.pricing_model === 'per_unit') &&
                                                                    limit > 0 && (
                                                                        <div className="pt-2 space-y-0.5">
                                                                            <div className="flex justify-between text-xs text-slate-500">
                                                                                <span>Usage</span>
                                                                                <span>
                                                                                    {used.toLocaleString()} /{' '}
                                                                                    {limit.toLocaleString()}
                                                                                </span>
                                                                            </div>
                                                                            <Progress
                                                                                value={pct}
                                                                                className={`h-1.5 ${
                                                                                    pct >= 90
                                                                                        ? '[&>div]:bg-red-500'
                                                                                        : pct >= 70
                                                                                          ? '[&>div]:bg-amber-500'
                                                                                          : ''
                                                                                }`}
                                                                            />
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </Card>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Add Feature */}
                            {isActive(detailSub?.status ?? '') &&
                                availableFeatures.length > 0 && (
                                    <>
                                        <Separator />
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-900 mb-3">
                                                Add Feature
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={addFeatureId}
                                                    onValueChange={setAddFeatureId}
                                                >
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Select a feature to add..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {availableFeatures.map((f) => (
                                                            <SelectItem
                                                                key={f.id}
                                                                value={f.id}
                                                            >
                                                                <span className="flex items-center gap-2">
                                                                    {f.name ||
                                                                        f.capability_name ||
                                                                        f.feature_name ||
                                                                        f.capability_key}
                                                                    <span className="text-xs text-muted-foreground">
                                                                        ({pricingLabel(f)})
                                                                    </span>
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    onClick={handleAddFeature}
                                                    disabled={!addFeatureId || addingFeature}
                                                >
                                                    {addingFeature ? (
                                                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                                    ) : (
                                                        <Plus className="h-4 w-4 mr-1" />
                                                    )}
                                                    Add
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}

                            {/* API Keys — credentials for calling this application's capabilities */}
                            <div>
                                <div className="mb-3 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-900">API Keys</h3>
                                        <p className="text-xs text-muted-foreground">
                                            Client credentials for calling capabilities of this application.
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        className="gap-1.5"
                                        disabled={!detailSub || !isActive(detailSub.status)}
                                        onClick={() => setCreateKeyOpen(true)}
                                    >
                                        <Plus size={14} />
                                        Create Key
                                    </Button>
                                </div>
                                {detailSub && !isActive(detailSub.status) && (
                                    <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                        <p className="text-sm text-amber-800">
                                            Subscription is not active. New keys cannot be created.
                                        </p>
                                    </div>
                                )}
                                {keysLoading ? (
                                    <div className="flex items-center justify-center py-8 gap-2">
                                        <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                                        <span className="text-sm text-muted-foreground">Loading keys...</span>
                                    </div>
                                ) : subKeys.length === 0 ? (
                                    <Card className="border border-dashed border-slate-200 bg-white shadow-sm">
                                        <CardContent className="py-10 text-center">
                                            <Key className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                                            <p className="text-sm text-muted-foreground">
                                                No API keys yet. Create one to call this application programmatically.
                                            </p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <div className="space-y-2">
                                        {subKeys.map((k) => (
                                            <div
                                                key={k.id}
                                                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-medium text-slate-900">
                                                        {k.client_name}
                                                    </p>
                                                    {k.description && (
                                                        <p className="truncate text-xs text-slate-500">
                                                            {k.description}
                                                        </p>
                                                    )}
                                                    <p className="mt-0.5 font-mono text-xs text-slate-400">
                                                        {k.client_id}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs text-slate-400">
                                                        {new Date(k.created_at).toLocaleDateString()}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-700"
                                                        onClick={() => setRevokeKeyId(k.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {detailSub && isActive(detailSub.status) && !loadingDetail && (
                        <div className="shrink-0 border-t border-slate-200 px-8 py-4">
                            <div className="flex w-full items-center justify-between gap-4">
                                <p className="text-xs text-slate-500">
                                    Canceling keeps the subscription active until the end of the
                                    current billing period.
                                </p>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() =>
                                        detailSub && setCancelTarget(detailSub)
                                    }
                                >
                                    Cancel Subscription
                                </Button>
                            </div>
                        </div>
                    )}
            </SlideDrawer>

            {/* Create subscriber API key */}
            <Sheet open={createKeyOpen} onOpenChange={(open) => !open && closeCreateKey()}>
                <SheetContent className="sm:max-w-lg">
                    <SheetHeader>
                        <SheetTitle>{keyCredentials ? 'Key Created' : 'Create API Key'}</SheetTitle>
                        <SheetDescription>
                            {keyCredentials
                                ? 'Copy credentials now — the secret cannot be retrieved later.'
                                : 'Create a client credential for calling capabilities of this application.'}
                        </SheetDescription>
                    </SheetHeader>
                    {!keyCredentials ? (
                        <div className="space-y-4 p-4">
                            <div className="space-y-2">
                                <Label htmlFor="sub-key-name">Display name</Label>
                                <Input
                                    id="sub-key-name"
                                    placeholder="e.g. Production Backend"
                                    value={createKeyName}
                                    onChange={(e) => setCreateKeyName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sub-key-desc">Description (optional)</Label>
                                <Input
                                    id="sub-key-desc"
                                    placeholder="What this key is used for..."
                                    value={createKeyDesc}
                                    onChange={(e) => setCreateKeyDesc(e.target.value)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 p-4">
                            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                                <p className="text-sm text-amber-800">
                                    Store the secret securely. This is the only time it will be shown.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Client ID</Label>
                                <div className="flex gap-2">
                                    <Input
                                        readOnly
                                        value={keyCredentials.client_id}
                                        className="font-mono text-sm"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                            copyKeyCredential(keyCredentials.client_id, 'id')
                                        }
                                    >
                                        {copiedId ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Client Secret</Label>
                                <div className="flex gap-2">
                                    <Input
                                        readOnly
                                        value={keyCredentials.client_secret}
                                        className="font-mono text-sm"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                            copyKeyCredential(keyCredentials.client_secret, 'secret')
                                        }
                                    >
                                        {copiedSecret ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                    <SheetFooter className="px-4 pb-4">
                        {!keyCredentials ? (
                            <Button
                                onClick={handleCreateKey}
                                disabled={creatingKey || !createKeyName.trim()}
                                className="w-full gap-2"
                            >
                                {creatingKey ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                    <Key size={16} />
                                )}
                                {creatingKey ? 'Creating...' : 'Create Key'}
                            </Button>
                        ) : (
                            <Button onClick={closeCreateKey} className="w-full">
                                Done
                            </Button>
                        )}
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Revoke subscriber key */}
            <AlertDialog open={!!revokeKeyId} onOpenChange={(open) => !open && setRevokeKeyId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will immediately invalidate the key. Any applications using it will
                            stop working. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={revokingKey}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRevokeKey}
                            disabled={revokingKey}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {revokingKey ? 'Revoking...' : 'Revoke'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ----------------------------------------------------------------- */}
            {/* Cancel Confirmation Dialog                                          */}
            {/* ----------------------------------------------------------------- */}
            <AlertDialog
                open={!!cancelTarget}
                onOpenChange={(open) => !open && setCancelTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to cancel your subscription to{' '}
                            <span className="font-medium text-slate-700">
                                {cancelTarget ? productName(cancelTarget.product_id) : ''}
                            </span>
                            ? It will remain active until the end of the current billing period.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={canceling}>
                            Keep Subscription
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={handleCancel}
                            disabled={canceling}
                        >
                            {canceling ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                    Canceling...
                                </>
                            ) : (
                                'Cancel Subscription'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
